import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // iOS觸控閘門(2026-08-13):CSS overscroll-behavior在iOS實機不足以擋
    // 「按固定區拖動→文件橡皮筋→商品捲動區失去觸控」(body-scroll-lock同款技術)。
    // 只放行商品捲動區內且未到邊界的垂直手勢,其餘touchmove一律preventDefault,
    // 讓iOS根本沒機會把手勢外溢到文件層。passive:false必要,否則preventDefault無效。
    const { dialog } = this.refs;
    dialog?.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    dialog?.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    const { dialog } = this.refs;
    dialog?.removeEventListener('touchstart', this.#handleTouchStart);
    dialog?.removeEventListener('touchmove', this.#handleTouchMove);
  }

  #touchStartY = 0;
  #touchStartX = 0;

  #handleTouchStart = (event) => {
    this.#touchStartY = event.touches[0]?.clientY ?? 0;
    this.#touchStartX = event.touches[0]?.clientX ?? 0;
  };

  #handleTouchMove = (event) => {
    const { dialog } = this.refs;
    if (!dialog?.open || event.touches.length > 1) return;

    const touch = event.touches[0];
    const deltaY = (touch?.clientY ?? 0) - this.#touchStartY;
    const deltaX = (touch?.clientX ?? 0) - this.#touchStartX;

    // 8px門檻(2026-08-13):iOS點按幾乎都帶1〜3px微移動,無門檻的
    // preventDefault會讓iOS取消該次tap的click合成——實機表現=固定區
    // 按鈕「沒反應」(body-scroll-lock已知副作用,桌面模擬測不出)。
    // 未過門檻=點按雜訊一律放行;過門檻=真拖動才進入攔截判斷。
    // (固定區CSS為touch-action:manipulation——僅去雙擊縮放延遲;
    //  none會讓iOS不合成click,禁用。拖動攔截由本閘門全權負責)
    if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 8) return;

    const scroller = dialog.querySelector('.cart-drawer__items');
    const target = event.target;

    if (!scroller || !(target instanceof Node) || !scroller.contains(target)) {
      event.preventDefault();
      return;
    }

    // 方向感知(2026-08-14 C版滑軌需要):捲動區內「水平為主」的手勢放行
    // (推薦滑軌的橫向捲動;橫向外溢由rail的overscroll-behavior-x:contain擋),
    // 只有垂直為主的手勢才進下方垂直邊界攔截判斷
    if (Math.abs(deltaX) > Math.abs(deltaY)) return;

    const cannotScroll = scroller.scrollHeight <= scroller.clientHeight + 1;
    const atTop = scroller.scrollTop <= 0;
    const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1;

    if (cannotScroll || (atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
      event.preventDefault();
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

// クーポン開合防彈(2026-08-13):實機iOS Safari對inert屬性/兄弟選擇器的
// 樣式重算不可靠(點擊後aria有翻、面板不展開)。最終機制=每次開合把面板
// 節點clone後以正確狀態(.is-open class+inert屬性)原位重插——全新節點
// 樣式從零計算,不走失效重算。涵蓋抽屜+/cart頁,載入時同步一次(折扣碼
// 已套用時server-render為展開態)。
const syncDisclosureOpenClass = () => {
  const triggers = document.querySelectorAll(':is(.cart-drawer, .cart-page) .disclosure-trigger');
  for (const trigger of triggers) {
    const content = trigger.nextElementSibling;
    if (!(content instanceof Element) || !content.classList.contains('disclosure-content')) continue;

    const open = trigger.getAttribute('aria-expanded') === 'true';

    // 移除→原位重插(2026-08-13用戶指示):全新節點的樣式從零計算,
    // 完全繞過iOS Safari的失效重算bug(class/屬性怎麼掛都不重算的裝置也有效)。
    // 代價=開合無過渡動畫。事件皆為document層委派+custom element自動升級,
    // clone不掉功能;inert屬性由此處接管(核心JS的stale ref寫入無害,
    // 每次點擊都會被本函式以aria為準覆蓋)。
    const fresh = content.cloneNode(true);
    fresh.classList.toggle('is-open', open);
    if (open) {
      fresh.removeAttribute('inert');
    } else {
      fresh.setAttribute('inert', '');
    }
    content.replaceWith(fresh);
  }
};

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  if (!event.target.closest(':is(.cart-drawer, .cart-page) .disclosure-trigger')) return;
  // 核心toggleDisclosure在document層delegation執行,rAF確保翻完aria後才同步
  requestAnimationFrame(syncDisclosureOpenClass);
});

// 點圖加購回饋(2026-08-14 skills依據:tap-feedback-speed/success-feedback):
// 圖片submit若走form屬性原生提交=繞過追加鈕的atc-added動畫鏈,點了沒回饋。
// 改為轉發:攔下圖片鈕的提交,改觸發同圖磚的追加鈕click——既有換字動畫+AJAX
// 全鏈重用;tile加.is-adding=圖片上藍圈白勾確認(CSS)。is-adding兼防連點,
// morph換新DOM時自然清除。form屬性保留=JS失效時的無腳本後備。
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const imgbtn = event.target.closest('.cart-rec-tile__imgbtn[type="submit"]');
  if (!imgbtn) return;
  event.preventDefault();
  const tile = imgbtn.closest('.cart-rec-tile');
  if (!tile || tile.classList.contains('is-adding')) return;
  const addButton = tile.querySelector('.cart-xsell__btn');
  if (!(addButton instanceof HTMLElement)) return;
  tile.classList.add('is-adding');
  addButton.click();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncDisclosureOpenClass, { once: true });
} else {
  syncDisclosureOpenClass();
}
